"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const RULES = [
  {
    topic: "Who sees this",
    rule: "Admin, and only if the company has an applicable e-invoice / e-way feature (E-invoice and e-way bill, or e-way bill only). Otherwise the card is not on All settings",
  },
  {
    topic: "What these credentials are",
    rule: "NIC e-invoice API username and password you created after registering the GSP. Not the GSTIN on Company Profile, and not your ABSS login",
  },
  {
    topic: "Password on screen",
    rule: "Saved password is never shown in the form. View mode: Configured + Show password (loads it) / Hide. On Edit, leave password blank to keep the current one",
  },
  {
    topic: "Delete",
    rule: "Confirm: E-invoice and e-way bill generation will stop working until new credentials are added",
  },
];

const sections = [
  {
    heading: "What this is for",
    subsections: [
      { heading: "1. Why it exists" },
      { heading: "2. Add, view, edit, delete" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

const Steps = ({ items }) => (
  <ol className="space-y-3 mb-4">
    {items.map((text, i) => (
      <li key={i} className="flex items-start gap-3 list-none">
        <span className="w-6 h-6 rounded-full bg-[#111827] text-white text-[12px] geist-medium flex items-center justify-center flex-shrink-0 mt-0.5">
          {i + 1}
        </span>
        <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
          {text}
        </p>
      </li>
    ))}
  </ol>
);

const Flow = ({ steps }) => (
  <p className="text-[15px] geist-regular text-[#374151] leading-relaxed mb-4 px-4 py-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-[10px]">
    {steps.map((step, i) => (
      <span key={`${step}-${i}`}>
        {i > 0 && (
          <span className="text-[#9CA3AF] geist-medium"> {"→"} </span>
        )}
        <span className="geist-medium text-[#111827]">{step}</span>
      </span>
    ))}
  </p>
);

export default function GstPortalCredentialsPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            GST Portal Credentials
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Admins — NIC portal login so Invoice can generate IRN and
            e-way bills
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            Title <B>GST portal credentials</B>. Subtitle: Portal login used
            to generate e-invoices and e-way bills. Without this saved,
            Generate IRN / e-way on an Invoice cannot talk to the portal.
          </p>
          <Flow
            steps={[
              "Settings",
              "Organization",
              "GST portal credentials",
              "Add",
              "username + password",
              "Save",
            ]}
          />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this is for
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Why it exists
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            India e-invoice and e-way need a NIC API user you register once
            per GSTIN (Tax Payer → GSP → create username/password). The
            on-screen guide walks through that NIC registration. Then you
            store those two fields here.
          </p>
          <Definitions
            items={[
              {
                term: "What it affects",
                desc: "Invoice: generate and cancel IRN and/or e-way bill (whichever additional feature the company has). If credentials are missing or deleted, those Invoice actions fail until you add them again",
              },
              {
                term: "What it does not affect",
                desc: "Company GSTIN on Profile, or GST rates on items. Those are separate",
              },
            ]}
          />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Add, view, edit, delete
          </h3>
          <Steps
            items={[
              <>
                Empty state: guide + No GST portal credentials configured.
                Click <B>+ Add</B>.
              </>,
              <>
                Enter <B>Portal username</B> and <B>Portal password</B>{" "}
                (required on first save). Click <B>Save</B>.
              </>,
              <>
                After save you see username, Password as Configured (Show
                password / Hide), and Provider. <B>Edit</B> to change
                username; leave password blank to keep the current one.{" "}
                <B>Delete</B> if you must stop portal access.
              </>,
            ]}
          />
          <Screenshot caption="GST portal credentials — Add form with NIC guide, or view with Configured password and Show password" />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[10px] overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827] w-[28%]">
                    Topic
                  </th>
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827]">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((row) => (
                  <tr
                    key={row.topic}
                    className="border-b border-[#E5E5E5] last:border-b-0"
                  >
                    <td className="px-4 py-2.5 text-[14px] geist-medium text-[#111827] align-top">
                      {row.topic}
                    </td>
                    <td className="px-4 py-2.5 text-[14px] geist-regular text-[#374151]">
                      {row.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/settings/profile"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Profile <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Company GSTIN on documents — not this portal login
            </p>
          </Link>
          <Link
            href="/documentation/books/sales/invoices"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Invoices <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Where Generate IRN / e-way uses these credentials
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
